<script setup>
import flatPickr from "vue-flatpickr-component";
import 'flatpickr/dist/flatpickr.css'
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import {  ref, watch, onMounted, nextTick, onBeforeUnmount} from 'vue';
import moment from 'moment';
const props = defineProps({
  modelValue: {
    type: [String,Array],
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "dd/mm/aaaa",
  },
  size: {
    type: String,
    default: "default",
  },
  min: {
    type: String,
    default: null
  },
  max: {
    type: String,
    default: null
  },
  error:{
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'date',
  },
});
const emit = defineEmits(['update:modelValue'])
const selectedDate = ref(props.modelValue);
const flatpickrInstance = ref(null);
let removeCalendarGuards = null;
const config = ref({
  locale: Spanish,
  altInput: true,
  allowInput: true,
  dateFormat: "Y-m-d", 
  altFormat: "d/m/Y",
  wrap: true,
  onChange: (selectedDates, dateStr, instance) => {
    applyInvalidClass(instance);
    if(props.type=='range' && selectedDates.length==1){
      return;
    }
    if (selectedDate.value !== dateStr) {
      let  res = dateStr;
      if(props.type === 'range' && selectedDates.length === 2) {
        const startDate = moment(selectedDates[0]).format('YYYY-MM-DD');
        const endDate = moment(selectedDates[1]).format('YYYY-MM-DD');
        res = [`${startDate}`, `${endDate}`];
        if (startDate === endDate && instance.altInput) {
          const formatted = moment(selectedDates[0]).format('DD/MM/YYYY');
          instance.altInput.value = `${formatted} a ${formatted}`;
        }
      } 

      emit('update:modelValue', res);
      flatpickrInstance.value = instance;
    }
  },
  onReady: (selectedDates, dateStr, instance) => {
    flatpickrInstance.value = instance;
    applyInvalidClass(instance);
    const cal = instance.calendarContainer;
    if (!cal) return;

    const stop = (e) => e.stopPropagation();
    cal.addEventListener("click", stop);
    
    removeCalendarGuards = () => {
      cal.removeEventListener("click", stop);
    };
  }
});
onBeforeUnmount(() => {
  removeCalendarGuards?.();
});

onMounted(() => {
  if(props.type ==='range'){
    config.value.mode= "range";
  }
  if(!props.disabled){
    config.value.minDate= props.min;
    config.value.maxDate= props.max;
  }
  nextTick(() => {
    const altInput = document.querySelector(`input.form-control.input[placeholder="${props.placeholder}"]`);
    if (altInput) {
      altInput.setAttribute('readonly', true);
    }
  });
});


watch(() => props.modelValue, (newVal) => {
  if (newVal !== selectedDate.value) {
    selectedDate.value = newVal;
  }
  nextTick(() => {
    if(flatpickrInstance.value){
      applyInvalidClass(flatpickrInstance.value);
    }
    if (flatpickrInstance.value && flatpickrInstance.value._input  &&  newVal && newVal != '' && !props.disabled) {
      // Forzar mostrar valor aunque no sea válido para flatpickr, en el caso que no sea lectura
      flatpickrInstance.value._input.value = moment(newVal).format('DD/MM/YYYY');
    }
  });
}, { immediate: true }); 

watch(() => props.error, () => {
  applyInvalidClass(flatpickrInstance.value);
  
}, { immediate: true });
function applyInvalidClass(instance) {
  const altInput = instance?.altInput;
  if (altInput) {
    if (props.error) {
      altInput.classList.add('is-invalid');
    } else {
      altInput.classList.remove('is-invalid');
    }
  }
}

</script>

<template>
  <div class="calendar-input-wrapper mb-2">
    <flat-pickr
      :id="id"
      ref="flatpickrInstance"
      :model-value="selectedDate"
      class="form-control"
      :class="[size ? `form-control-${size}` : '']"
      :config="config"
      :placeholder="placeholder"
      :disabled="disabled"
      />
    <i v-if="!disabled" class="fa-regular fa-calendar calendar-icon text-xs"></i>
  </div>
</template>

<style scoped>
:deep(.form-control) {
  background-color: #fff !important;
  cursor: pointer !important;
  font-size: 0.875rem !important;
}
:deep(.form-control:disabled, .form-control[readonly]) {
  cursor: auto !important;
}
:deep(.form-control.is-invalid) {
  background-image: none !important;
}
:deep(.flatpickr-input) {
  border: 1px solid !important;
}
.calendar-input-wrapper {
  position: relative;
  width: 100%;
}
.calendar-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--color-text-primary);
}
</style>

<style>
/* flatpickr calendar — global porque se renderiza en el <body> */
.flatpickr-calendar {
  border: 1px solid rgba(148, 163, 184, 0.24) !important;
  border-radius: 18px !important;
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.16), 0 8px 24px rgba(15, 23, 42, 0.08) !important;
  font-family: inherit !important;
  padding: 0.75rem !important;
  width: auto !important;
}
.flatpickr-calendar::before,
.flatpickr-calendar::after {
  display: none !important;
}

.flatpickr-months {
  display: flex !important;
  align-items: center !important;
  padding: 0.25rem 0 0.5rem !important;
}
.flatpickr-month {
  height: 2rem !important;
}
.flatpickr-current-month {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #0f172a !important;
  padding: 0 !important;
}
.flatpickr-current-month .flatpickr-monthDropdown-months {
  font-weight: 600 !important;
  color: #0f172a !important;
}
.flatpickr-current-month input.cur-year {
  font-weight: 600 !important;
  color: #0f172a !important;
}
.flatpickr-prev-month,
.flatpickr-next-month {
  position: relative !important;
  top: auto !important;
  transform: none !important;
  color: var(--color-text-primary) !important;
  fill: var(--color-text-primary) !important;
  padding: 5px 7px !important;
  border-radius: 0.5rem !important;
  transition: background 0.18s, color 0.18s !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: auto !important;
  margin-top: -8px !important;
}
.flatpickr-prev-month:hover,
.flatpickr-next-month:hover {
  background: rgba(148, 163, 184, 0.14) !important;
  color: #0f172a !important;
  fill: #0f172a !important;
}
.flatpickr-prev-month svg,
.flatpickr-next-month svg {
  width: 12px !important;
  height: 12px !important;
}

.flatpickr-weekdays {
  margin-bottom: 0.125rem !important;
}
.flatpickr-weekday {
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  color: rgba(148, 163, 184, 0.8) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
}

.flatpickr-day {
  border-radius: 0.5rem !important;
  font-size: 0.8125rem !important;
  font-weight: 400 !important;
  color: var(--color-text-primary) !important;
  border: none !important;
  transition: background 0.18s, color 0.18s !important;
}
.flatpickr-day:hover:not(.selected):not(.startRange):not(.endRange) {
  background: rgba(148, 163, 184, 0.14) !important;
  color: #0f172a !important;
}
.flatpickr-day.flatpickr-disabled,
.flatpickr-day.prevMonthDay,
.flatpickr-day.nextMonthDay {
  color: rgba(148, 163, 184, 0.4) !important;
}

.flatpickr-day.today {
  border: none !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  background: transparent !important;
  position: relative;
}
.flatpickr-day.today::after {
  content: '' !important;
  position: absolute !important;
  bottom: 3px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  width: 4px !important;
  height: 4px !important;
  border-radius: 50% !important;
  background: rgba(148, 163, 184, 0.8) !important;
}
.flatpickr-day.today.selected::after,
.flatpickr-day.today.startRange::after,
.flatpickr-day.today.endRange::after {
  background: #fff !important;
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange {
  background: #0f172a !important;
  color: #fff !important;
  border-radius: 0.5rem !important;
  border: none !important;
  font-weight: 600 !important;
}
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover {
  background: #0f172a !important;
  color: #fff !important;
}

.flatpickr-day.inRange {
  background: rgba(148, 163, 184, 0.14) !important;
  color: var(--color-text-primary) !important;
  border-radius: 0 !important;
  border: none !important;
  box-shadow: none !important;
}
.flatpickr-day.startRange:not(.endRange) {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.flatpickr-day.endRange:not(.startRange) {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>